/**
 * @module game/ComboPopup
 * Generates combo milestone popup labels and tracks their display lifetime.
 *
 * Popup text uses the pattern:
 *   combo 2 → "Double Slash!  x2"
 *   combo 3 → "Triple Slash!  x3"
 *   combo 4 → "Quad Slash!    x4"
 *   combo 5+ → "FRENZY SLASH! x5" (and above)
 *
 * Milestones only fire at exact thresholds (combo reaches 2, 3, 4, 5) to
 * avoid spamming a popup every single hit above 5. The render layer reads
 * {@link ComboPopupManager.active} each frame and draws the text until it expires.
 */

import { CONFIG } from '../game-config.js';

/** A popup event currently on screen. */
export interface ComboPopupEvent {
  /** Label text to display. */
  label: string;
  /** Timestamp (ms) when this popup was created — drives fade-out alpha. */
  createdAt: number;
  /** Combo count that triggered this popup. */
  combo: number;
}

/** Combo counts that trigger a popup. */
const POPUP_THRESHOLDS = new Set([2, 3, 4, 5]);

/** Human-readable prefix per combo milestone. */
const COMBO_PREFIX: Record<number, string> = {
  2: 'Double Slash!',
  3: 'Triple Slash!',
  4: 'Quad Slash!',
};

/**
 * Returns the popup label for the given combo count, or null if this count
 * does not trigger a milestone popup.
 */
export function comboLabel(combo: number): string | null {
  if (!POPUP_THRESHOLDS.has(combo) && combo < 5) return null;

  // For combo >= 5 trigger once at exactly 5; skip 6, 7, 8... to avoid spam.
  if (combo > 5) return null;

  const prefix = COMBO_PREFIX[combo] ?? 'FRENZY SLASH!';
  return `${prefix}  x${combo}`;
}

/**
 * Manages the lifecycle of combo popup events shown on screen.
 *
 * Call {@link ComboPopupManager.onCombo} whenever the combo increments.
 * Call {@link ComboPopupManager.tick} each frame to expire old popups.
 * Read {@link ComboPopupManager.active} to get current popup (or null).
 */
export class ComboPopupManager {
  private _current: ComboPopupEvent | null = null;

  /**
   * Notifies the manager of a new combo count.
   * Creates a popup event if this is a milestone combo.
   *
   * @param combo - Current combo count after the slash.
   * @param nowMs - Current timestamp (ms) from performance.now().
   */
  onCombo(combo: number, nowMs: number): void {
    const label = comboLabel(combo);
    if (label === null) return;

    this._current = { label, createdAt: nowMs, combo };
  }

  /**
   * Expires popups that have exceeded CONFIG.combo.popupFadeMs.
   *
   * @param nowMs - Current timestamp (ms) from performance.now().
   */
  tick(nowMs: number): void {
    if (this._current === null) return;
    if (nowMs - this._current.createdAt >= CONFIG.combo.popupFadeMs) {
      this._current = null;
    }
  }

  /** The currently displayed popup event, or null if none. */
  get active(): ComboPopupEvent | null {
    return this._current;
  }

  /** Dismisses any active popup immediately (e.g. on game over). */
  clear(): void {
    this._current = null;
  }
}
